'use client'

/**
 * Shared controls for collapsible reader side rails, paired with
 * `useCollapsibleRail`. Two pieces:
 *
 *  - RailCollapseButton — a small chevron button that lives at the top of an
 *    expanded rail; click to collapse that rail and hand its width to the
 *    article.
 *  - RailReopenHandle — a slim edge-docked pull-tab, shown only while the
 *    rail is collapsed, that brings it back. Deliberately mirrors the mobile
 *    TOC/Spec drawer tabs (fixed right-edge, rope-deep, vertical label) so
 *    the affordance reads the same across breakpoints. Desktop-only
 *    (`hidden lg:flex`) — below `lg` the rails are already a drawer.
 *
 * Both are presentational: the collapsed state + persistence live in the
 * consuming surface via `useCollapsibleRail`.
 */

import { useTranslations } from 'next-intl'
import {
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
} from 'lucide-react'

type Side = 'left' | 'right'

export function RailCollapseButton({
  side,
  label,
  onClick,
  className = '',
}: {
  /** Which rail this collapses — picks the chevron direction. */
  side: Side
  /** Localized panel name, for the accessible label (e.g. "Contents"). */
  label: string
  onClick: () => void
  className?: string
}) {
  const t = useTranslations('rails')
  const Icon = side === 'left' ? PanelLeftClose : PanelRightClose
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('collapse', { label })}
      title={t('collapse', { label })}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${className}`}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  )
}

export function RailReopenHandle({
  side,
  label,
  onClick,
}: {
  /** Which edge the tab docks to and which panel it reopens. */
  side: Side
  /** Localized short vertical label + accessible name (e.g. "Contents"). */
  label: string
  onClick: () => void
}) {
  const t = useTranslations('rails')
  const Icon = side === 'left' ? PanelLeftOpen : PanelRightOpen
  const edge =
    side === 'left' ? 'left-0 rounded-r-md' : 'right-0 rounded-l-md'
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('show', { label })}
      className={`fixed top-1/2 z-40 hidden -translate-y-1/2 flex-col items-center gap-1 px-1.5 py-3 text-white shadow-lg transition-[filter] hover:brightness-110 lg:flex ${edge}`}
      style={{ background: 'var(--rope-deep)' }}
    >
      <Icon className="h-4 w-4" aria-hidden />
      <span className="font-mono text-[9px] uppercase tracking-[0.14em] [writing-mode:vertical-rl] [text-orientation:mixed]">
        {label}
      </span>
    </button>
  )
}
